// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
/**
 * Custom MapLibre navigation control with zoom in, zoom out, and reset-to-bounds buttons.
 *
 * Implements the MapLibre `IControl` interface so it can be added to any MapLibre map
 * via `map.addControl(control, position)`. Designed to be reusable across projects.
 *
 * Visual style matches the default MapLibre NavigationControl (`.maplibregl-ctrl-group`).
 *
 * @example
 * import { NavigationControl } from "$lib/components/MapLibre/NavigationControl.js";
 *
 * const bounds = [[minX, minY], [maxX, maxY]];
 * const control = new NavigationControl({ bounds });
 * map.addControl(control, "bottom-right");
 */

// SVG icon for the zoom-in button — matches MapLibre's built-in plus icon
const ZOOM_IN_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29">
  <path d="M14.5 8.5c-.75 0-1.5.75-1.5 1.5v3h-3c-.75 0-1.5.75-1.5 1.5S9.25 16 10 16h3v3c0 .75.75 1.5 1.5 1.5S16 19.75 16 19v-3h3c.75 0 1.5-.75 1.5-1.5S19.75 13 19 13h-3v-3c0-.75-.75-1.5-1.5-1.5"/>
</svg>`;

// SVG icon for the zoom-out button — matches MapLibre's built-in minus icon
const ZOOM_OUT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29">
  <path d="M10 13c-.75 0-1.5.75-1.5 1.5S9.25 16 10 16h9c.75 0 1.5-.75 1.5-1.5S19.75 13 19 13z"/>
</svg>`;

// SVG bounding-box icon for the reset-to-bounds button
const RESET_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
  <g transform="translate(7, 7)">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M0 0H6V2H2V6H0V0Z" fill="black"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M15 0L15 6L13 6L13 2L9 2L9 -2.62268e-07L15 0Z" fill="black"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M15 15L9 15L9 13L13 13L13 9L15 9L15 15Z" fill="black"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M0 15L-2.62268e-07 9L2 9L2 13L6 13L6 15L0 15Z" fill="black"/>
  </g>
</svg>`;

/**
 * @typedef {[[number, number], [number, number]]} LngLatBoundsLike
 * A bounding box as [[minLng, minLat], [maxLng, maxLat]].
 */

/**
 * @typedef {Object} NavigationControlOptions
 * @property {LngLatBoundsLike} bounds - The bounds to reset the map to.
 * @property {import("maplibre-gl").FitBoundsOptions} [fitBoundsOptions] - Options passed to
 *   `map.fitBounds()` when resetting. Defaults to `{ padding: 20, duration: 300 }`.
 * @property {((map: any) => void) | null} [onZoomIn] - Optional callback invoked when the zoom-in
 *   button is clicked. Receives the MapLibre map instance. If omitted, defaults to `map.zoomIn()`.
 * @property {((map: any) => void) | null} [onZoomOut] - Optional callback invoked when the zoom-out
 *   button is clicked. Receives the MapLibre map instance. If omitted, defaults to `map.zoomOut()`.
 */

export class NavigationControl {
  /** @type {any} MapLibre map instance */
  _map = null;

  /** @type {HTMLDivElement | null} Root container element */
  _container = null;

  /** @type {LngLatBoundsLike} */
  _bounds;

  /** @type {import("maplibre-gl").FitBoundsOptions} */
  _fitBoundsOptions;

  /** @type {((map: any) => void) | null} */
  _onZoomIn;

  /** @type {((map: any) => void) | null} */
  _onZoomOut;

  /**
   * @param {NavigationControlOptions} options
   */
  constructor(options) {
    if (!options || !options.bounds) {
      throw new Error("NavigationControl requires a `bounds` option.");
    }

    this._bounds = options.bounds;
    this._fitBoundsOptions = options.fitBoundsOptions ?? { padding: 20, duration: 300 };
    this._onZoomIn = options.onZoomIn ?? null;
    this._onZoomOut = options.onZoomOut ?? null;
  }

  /**
   * Called by MapLibre when the control is added to a map.
   * Creates the DOM for the control and attaches event listeners.
   *
   * @param {any} map - The MapLibre map instance.
   * @returns {HTMLDivElement} The control's container element.
   */
  onAdd(map) {
    this._map = map;

    this._container = document.createElement("div");
    this._container.className = "maplibregl-ctrl maplibregl-ctrl-group";

    this._container.appendChild(
      this._createButton("zoom-in", "Zoom in", ZOOM_IN_ICON, () => {
        if (this._onZoomIn) {
          this._onZoomIn(this._map);
        } else {
          this._map.zoomIn();
        }
      })
    );

    this._container.appendChild(
      this._createButton("zoom-out", "Zoom out", ZOOM_OUT_ICON, () => {
        if (this._onZoomOut) {
          this._onZoomOut(this._map);
        } else {
          this._map.zoomOut();
        }
      })
    );

    this._container.appendChild(
      this._createButton("reset", "Reset to full extent", RESET_ICON, () => {
        this._map.fitBounds(this._bounds, this._fitBoundsOptions);
      })
    );

    return this._container;
  }

  /**
   * Called by MapLibre when the control is removed from a map.
   * Removes the DOM element and clears the map reference.
   */
  onRemove() {
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
    this._container = null;
    this._map = null;
  }

  /**
   * Update the bounds used when resetting the map view. Useful when the underlying
   * features change and the initial extent should be recalculated.
   *
   * @param {LngLatBoundsLike} bounds - New bounding box.
   */
  setBounds(bounds) {
    this._bounds = bounds;
  }

  /**
   * Creates a single control button.
   *
   * @param {string} name - Short identifier used in the CSS class name.
   * @param {string} label - Human-readable label for `aria-label` and `title`.
   * @param {string} svgIcon - SVG markup string for the button icon.
   * @param {() => void} onClick - Click handler.
   * @returns {HTMLButtonElement}
   */
  _createButton(name, label, svgIcon, onClick) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `maplibregl-ctrl-${name}`;
    button.setAttribute("aria-label", label);
    button.title = label;

    const icon = document.createElement("span");
    icon.className = "maplibregl-ctrl-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = svgIcon;

    // Style the SVG to fill the icon span and inherit the control's color conventions
    const svg = icon.querySelector("svg");
    if (svg) {
      svg.style.width = "100%";
      svg.style.height = "100%";
      svg.style.fill = "#333";
      svg.style.display = "block";
    }

    button.appendChild(icon);
    button.addEventListener("click", onClick);

    return button;
  }
}
